/**
 * "Portfolio" — the brands and manufacturers this organization owns.
 *
 * Read-only, and deliberately so: ownership is recorded ON the brand or
 * manufacturer document, so the place to change it is that document's editor.
 * This section shows what points here and offers a way to go there.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ParentOrganization } from '../../types';
import { getEntitiesByParentOrgId } from '../../services/firebase';
import {
  getParentOrgPortfolio,
  getParentOrgUsageCounts,
  isAlgoliaConfigured,
} from '../../services/algolia';
import {
  isEmptyPortfolio,
  mergePortfolio,
  portfolioFacetValues,
  type Portfolio,
  type PortfolioEntry,
} from './parentOrgPortfolio';

interface Props {
  org: ParentOrganization;
  onOpenBrand?: (brandId: string) => void;
  onOpenManufacturer?: (manufacturerId: string) => void;
}

export function PortfolioSection({ org, onOpenBrand, onOpenManufacturer }: Props) {
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
  const [loading, setLoading] = useState(false);
  const [indexUnavailable, setIndexUnavailable] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setIndexUnavailable(false);
    try {
      // The FK half runs whatever happens; the legacy half needs the index.
      const fkPromise = getEntitiesByParentOrgId(org.id);

      let legacy: Portfolio = { brands: [], manufacturers: [] };
      if (isAlgoliaConfigured()) {
        try {
          const usage = await getParentOrgUsageCounts();
          // One list for both indexes: a string used on a brand and a
          // manufacturer appears in both facet maps, and is sent once.
          const values = portfolioFacetValues(org, [
            ...new Set([...Object.keys(usage.brands), ...Object.keys(usage.manufacturers)]),
          ]);
          const hits = await getParentOrgPortfolio(values);
          const toEntry = (h: { id: string; name: string; matchedValue: string }): PortfolioEntry => ({
            id: h.id,
            name: h.name,
            via: 'parentOrg',
            matchedValue: h.matchedValue,
          });
          legacy = {
            brands: hits.brands.map(toEntry),
            manufacturers: hits.manufacturers.map(toEntry),
          };
        } catch (err) {
          // Fails SOFT, like the listing's counts: the FK half still renders.
          console.warn('Portfolio search unavailable:', err);
          setIndexUnavailable(true);
        }
      } else {
        setIndexUnavailable(true);
      }

      const fk = await fkPromise;
      setPortfolio(
        mergePortfolio(
          {
            brands: fk.brands.map((b) => ({ ...b, via: 'parentOrgId' as const })),
            manufacturers: fk.manufacturers.map((m) => ({ ...m, via: 'parentOrgId' as const })),
          },
          legacy,
        ),
      );
    } catch (err) {
      console.error('Failed to load portfolio:', err);
      setPortfolio({ brands: [], manufacturers: [] });
    } finally {
      setLoading(false);
    }
  }, [org]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Portfolio</h3>
        <span className="text-xs text-gray-500">
          What points at this organization — edit ownership on the brand or manufacturer itself
        </span>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="ml-auto rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loading && !portfolio ? (
        <p className="py-4 text-center text-sm italic text-gray-500">Loading portfolio…</p>
      ) : !portfolio || isEmptyPortfolio(portfolio) ? (
        <p className="rounded border border-dashed border-gray-200 py-4 text-center text-sm italic text-gray-500">
          No brands or manufacturers reference this organization yet.
          {indexUnavailable && ' (Search index unavailable — only direct links are shown.)'}
        </p>
      ) : (
        <div className="space-y-3">
          <Group
            label="Brands"
            entries={portfolio.brands}
            onOpen={onOpenBrand}
            emptyHint="No brands"
          />
          <Group
            label="Manufacturers"
            entries={portfolio.manufacturers}
            onOpen={onOpenManufacturer}
            emptyHint="No manufacturers"
          />
          {indexUnavailable && (
            <p className="text-xs text-amber-700">
              Search index unavailable — this list may be incomplete.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Group({
  label,
  entries,
  onOpen,
  emptyHint,
}: {
  label: string;
  entries: PortfolioEntry[];
  onOpen?: (id: string) => void;
  emptyHint: string;
}) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
        {label} ({entries.length})
      </p>
      {entries.length === 0 ? (
        <p className="text-xs italic text-gray-400">{emptyHint}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {entries.map((entry) => {
            // A chip only looks clickable when it can actually open something:
            // a dead chip that styles like a link is worse than a plain label.
            const clickable = !!onOpen;
            const title = entry.matchedValue
              ? `${entry.id} — matched as “${entry.matchedValue}”`
              : `${entry.id} — linked by parentOrgId`;
            return (
              <button
                key={entry.id}
                type="button"
                disabled={!clickable}
                onClick={clickable ? () => onOpen(entry.id) : undefined}
                title={title}
                className={`rounded-full border px-2 py-0.5 text-xs ${
                  clickable
                    ? 'cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-brand-400 hover:bg-brand-50'
                    : 'cursor-default border-gray-200 bg-gray-50 text-gray-600'
                }`}
              >
                {entry.name}
                {entry.via === 'parentOrgId' && (
                  <span className="ml-1 text-[10px] text-brand-600" title="Linked by parentOrgId">
                    ●
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
